import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, from, map, Observable, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

import { buildSearchParams } from '../_helpers/utils';
import { APIResponse } from '../_types/api-responses.types';
import {
  BillType,
  CreateInvoiceRequest,
  CreateInvoiceResponse,
  CreateReminderRequest,
  CreateReminderResponse,
  InvoiceDetailResponse,
  InvoiceExportRequest,
  InvoicePageRequest,
  InvoicePageResponse,
  ProcessInvoiceRequest,
  ProcessReminderRequest,
  ReminderDetailResponse,
  ReminderType,
  SearchProposalInvoicesResponse,
  UpdateInvoiceRequest,
  UpdateReminderRequest,
} from '../_types/billing.types';
import { UserAuthService } from './user-auth.service';

/**
 * A letter is fetched as a blob, so the backend's JSON error arrives as one too: parse it back so
 * callers can show its message. A body that is not JSON leaves the error untouched.
 */
function parseBlobError(err: HttpErrorResponse): Observable<never> {
  if (!(err.error instanceof Blob)) return throwError(() => err);
  return from(err.error.text()).pipe(
    map((text) => JSON.parse(text)),
    catchError(() => throwError(() => err)),
    switchMap((error) =>
      throwError(
        () =>
          new HttpErrorResponse({
            error,
            headers: err.headers,
            status: err.status,
            statusText: err.statusText,
            url: err.url ?? undefined,
          })
      )
    )
  );
}

@Injectable({
  providedIn: 'root',
})
export class BillingService {
  private readonly baseURL = environment.INSURANCEAPIURL;

  private readonly baseHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
    Role: 'ADMIN',
  });

  constructor(
    private readonly httpClient: HttpClient,
    private readonly userAuthService: UserAuthService
  ) {}

  createInvoice(body: CreateInvoiceRequest): Observable<CreateInvoiceResponse> {
    const headers = this.multipartHeaders();

    const formData = new FormData();
    formData.append('amount', String(body.amount));
    formData.append('type', body.type);
    formData.append('status', body.status);
    formData.append('receptionDate', body.receptionDate);
    formData.append('startDate', body.startDate);
    formData.append('endDate', body.endDate);
    formData.append('billingDate', body.billingDate);
    if (body.proposalId) formData.append('proposalId', body.proposalId);
    if (body.policyId) formData.append('policyId', body.policyId);
    if (body.file) formData.append('file', body.file);

    return this.httpClient.post<CreateInvoiceResponse>(
      `${this.baseURL}invoice`,
      formData,
      { headers }
    );
  }

  createReminder(
    body: CreateReminderRequest
  ): Observable<CreateReminderResponse> {
    const headers = this.multipartHeaders();

    const formData = this.buildReminderFormData(body);

    return this.httpClient.post<CreateReminderResponse>(
      `${this.baseURL}reminder`,
      formData,
      { headers }
    );
  }

  getInvoicesPage(body: InvoicePageRequest): Observable<InvoicePageResponse> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );

    const searchParams = buildSearchParams(body);

    return this.httpClient.get<InvoicePageResponse>(
      `${this.baseURL}invoices/page?${searchParams.toString()}`,
      { headers }
    );
  }

  /** Downloads the invoices and reminders matching the filters as a CSV file. */
  exportInvoicesCsv(body: InvoiceExportRequest): Observable<Blob> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );

    const searchParams = buildSearchParams(body);

    return this.httpClient.get(
      `${this.baseURL}invoices/export?${searchParams.toString()}`,
      { headers, responseType: 'blob' }
    );
  }

  getInvoiceDetail(invoiceId: string): Observable<InvoiceDetailResponse> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );
    return this.httpClient.get<InvoiceDetailResponse>(
      `${this.baseURL}invoice/${invoiceId}`,
      { headers }
    );
  }

  searchProposalInvoices(
    proposalId: string,
    query: string,
    limit = 20
  ): Observable<SearchProposalInvoicesResponse> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );
    const searchParams = buildSearchParams({ limit });
    searchParams.set('query', query);
    return this.httpClient.get<SearchProposalInvoicesResponse>(
      `${this.baseURL}proposal/${proposalId}/invoices?${searchParams.toString()}`,
      { headers }
    );
  }

  getReminderDetail(reminderId: string): Observable<ReminderDetailResponse> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );
    return this.httpClient.get<ReminderDetailResponse>(
      `${this.baseURL}reminder/${reminderId}`,
      { headers }
    );
  }

  attachCustomer(
    invoiceId: string,
    familyMemberId: string
  ): Observable<APIResponse<null>> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );
    return this.httpClient.patch<APIResponse<null>>(
      `${this.baseURL}invoice/${invoiceId}/attach-customer`,
      { familyMemberId },
      { headers }
    );
  }

  attachProposal(
    invoiceId: string,
    proposalId: string
  ): Observable<APIResponse<null>> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );
    return this.httpClient.patch<APIResponse<null>>(
      `${this.baseURL}invoice/${invoiceId}/attach-proposal`,
      { proposalId },
      { headers }
    );
  }

  attachReminderCustomer(
    reminderId: string,
    familyMemberId: string
  ): Observable<APIResponse<null>> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );
    return this.httpClient.patch<APIResponse<null>>(
      `${this.baseURL}reminder/${reminderId}/attach-customer`,
      { familyMemberId },
      { headers }
    );
  }

  attachReminderProposal(
    reminderId: string,
    proposalId: string
  ): Observable<APIResponse<null>> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );
    return this.httpClient.patch<APIResponse<null>>(
      `${this.baseURL}reminder/${reminderId}/attach-proposal`,
      { proposalId },
      { headers }
    );
  }

  attachReminderInvoice(
    reminderId: string,
    invoiceId: string
  ): Observable<APIResponse<null>> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );
    return this.httpClient.patch<APIResponse<null>>(
      `${this.baseURL}reminder/${reminderId}/attach-invoice`,
      { invoiceId },
      { headers }
    );
  }

  processInvoice(
    invoiceId: string,
    body: ProcessInvoiceRequest
  ): Observable<APIResponse<null>> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );
    return this.httpClient.patch<APIResponse<null>>(
      `${this.baseURL}invoice/${invoiceId}/process`,
      body,
      { headers }
    );
  }

  processReminder(
    reminderId: string,
    body: ProcessReminderRequest
  ): Observable<APIResponse<null>> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );
    return this.httpClient.patch<APIResponse<null>>(
      `${this.baseURL}reminder/${reminderId}/process`,
      body,
      { headers }
    );
  }

  generateInvoiceLetter(invoiceId: string): Observable<Blob> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );
    return this.httpClient
      .get(`${this.baseURL}invoice/${invoiceId}/letter`, {
        headers,
        responseType: 'blob',
      })
      .pipe(catchError(parseBlobError));
  }

  generateReminderLetter(reminderId: string): Observable<Blob> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );
    return this.httpClient
      .get(`${this.baseURL}reminder/${reminderId}/letter`, {
        headers,
        responseType: 'blob',
      })
      .pipe(catchError(parseBlobError));
  }

  updateInvoice(
    invoiceId: string,
    body: UpdateInvoiceRequest
  ): Observable<APIResponse<null>> {
    const headers = this.multipartHeaders();

    const formData = new FormData();
    formData.append('amount', String(body.amount));
    formData.append('type', body.type);
    formData.append('receptionDate', body.receptionDate);
    formData.append('startDate', body.startDate);
    formData.append('endDate', body.endDate);
    formData.append('billingDate', body.billingDate);
    if (body.proposalId) formData.append('proposalId', body.proposalId);
    if (body.policyId) formData.append('policyId', body.policyId);
    if (body.file) formData.append('file', body.file);

    return this.httpClient.put<APIResponse<null>>(
      `${this.baseURL}invoice/${invoiceId}`,
      formData,
      { headers }
    );
  }

  updateReminder(
    reminderId: string,
    body: UpdateReminderRequest
  ): Observable<APIResponse<null>> {
    const headers = this.multipartHeaders();

    return this.httpClient.put<APIResponse<null>>(
      `${this.baseURL}reminder/${reminderId}`,
      this.buildReminderFormData(body),
      { headers }
    );
  }

  updateReminderType(
    reminderId: string,
    type: ReminderType
  ): Observable<APIResponse<null>> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );
    return this.httpClient.put<APIResponse<null>>(
      `${this.baseURL}reminder/${reminderId}/type`,
      { type },
      { headers }
    );
  }

  updateInvoiceType(
    invoiceId: string,
    type: BillType
  ): Observable<APIResponse<null>> {
    const headers = this.baseHeaders.append(
      'Access-Token',
      this.userAuthService.getCurrentUser().token
    );
    return this.httpClient.put<APIResponse<null>>(
      `${this.baseURL}invoice/${invoiceId}/type`,
      { type },
      { headers }
    );
  }

  private multipartHeaders(): HttpHeaders {
    return this.baseHeaders
      .append('Access-Token', this.userAuthService.getCurrentUser().token)
      .delete('Content-Type');
  }

  private buildReminderFormData(
    body: CreateReminderRequest | UpdateReminderRequest
  ): FormData {
    const formData = new FormData();
    formData.append('customerId', body.customerId);
    formData.append('proposalId', body.proposalId);
    if (body.originalInvoiceId)
      formData.append('originalInvoiceId', body.originalInvoiceId);
    formData.append('type', body.type);
    formData.append('amount', String(body.amount));
    formData.append('receptionDate', body.receptionDate);
    formData.append('billingDate', body.billingDate);
    if (body.startDate) formData.append('startDate', body.startDate);
    if (body.endDate) formData.append('endDate', body.endDate);
    if (body.file) formData.append('file', body.file);
    return formData;
  }
}
